import * as React from 'react';
import { Switch as SwitchPrimitive } from 'radix-ui';

import { cn } from '@/lib/core';

interface SwitchProps {
  id?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  className?: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
  [key: string]: unknown;
}

function Switch({
  id,
  className,
  label,
  description,
  labelClassName,
  wrapperClassName,
  disabled,
  ...props
}: SwitchProps) {
  const autoId = React.useId();
  const resolvedId = id ?? autoId;

  const root = (
    <SwitchPrimitive.Root
      id={resolvedId}
      data-slot="switch"
      disabled={disabled}
      className={cn(
        'inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors',
        'bg-jncquoi-neutral-400',
        'data-[state=checked]:bg-jncquoi-neutral-800',
        'data-disabled:bg-jncquoi-neutral-100! data-disabled:cursor-not-allowed',
        'focus-visible:ring-jncquoi-neutral-1000 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        className
      )}
      {...(props as any)}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block size-4 rounded-full bg-white transition-transform',
          'data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
          'data-disabled:bg-jncquoi-neutral-700'
        )}
      />
    </SwitchPrimitive.Root>
  );

  if (!label && !description) {
    return root;
  }

  return (
    <div className={cn('flex items-start gap-2', wrapperClassName)}>
      {root}
      <label
        htmlFor={resolvedId}
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-1',
          disabled ? 'cursor-default' : 'cursor-pointer',
          labelClassName
        )}
      >
        {label && <span className="text-[14px] leading-normal tracking-[0.5px]">{label}</span>}
        {description && (
          <div className="font-pp-neue-montreal text-[12px] leading-normal tracking-[0.5px] text-[#848484]">
            {description}
          </div>
        )}
      </label>
    </div>
  );
}

export { Switch };
